/**
 * AdminSubscriptionPlans — Admin UI to edit the plan catalogue.
 *
 * Each of the four plans (free / starter / pro / enterprise) is rendered
 * as a card with inline editable fields:
 *   · name, price (INR + USD)
 *   · seats, job quota, candidates quota
 *   · marketing bullets (comma-per-line)
 *   · default feature preset (dropdown)
 *   · "Most popular" ribbon toggle
 *
 * "Save" writes an override doc; the base defaults (in
 * routers/subscriptions.py::PLANS) survive untouched. "Reset" drops all
 * overrides so the plan falls back to code defaults — useful when a
 * launch promo ends and you want the original catalogue back.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, Sparkles, DollarSign, Users, Briefcase, Layers, Star, Eye, EyeOff, Users2 } from "lucide-react";

const NUMERIC_FIELDS = ["price_inr", "price_usd", "seats", "job_quota", "candidates_quota"];

export default function AdminSubscriptionPlans() {
    const [plans, setPlans] = useState([]);
    const [presets, setPresets] = useState([]);
    const [drafts, setDrafts] = useState({});  // key → in-flight edits
    const [saving, setSaving] = useState(null);
    const [resetting, setResetting] = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/subscription-plans");
            setPlans(data.plans || []);
            setPresets(data.presets || []);
            const d = {};
            (data.plans || []).forEach((p) => { d[p.key] = { ...p.effective }; });
            setDrafts(d);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }

    useEffect(() => { load(); }, []);

    function setField(key, field, value) {
        setDrafts((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));
    }

    async function save(key) {
        setSaving(key);
        try {
            const src = drafts[key] || {};
            const body = {};
            // Send only editable fields — never overwrite spotlight or
            // internal metadata via this endpoint.
            ["name", "default_preset_key"].forEach((f) => {
                if (src[f] !== undefined) body[f] = src[f];
            });
            NUMERIC_FIELDS.forEach((f) => {
                if (src[f] !== undefined && src[f] !== null && src[f] !== "") {
                    body[f] = Number(src[f]);
                }
            });
            if (Array.isArray(src.features)) body.features = src.features;
            if (src.popular !== undefined) body.popular = !!src.popular;
            if (src.is_public !== undefined) body.is_public = !!src.is_public;
            if (src.spotlight && typeof src.spotlight === "object") body.spotlight = src.spotlight;
            await api.put(`/admin/subscription-plans/${key}`, body);
            toast.success(`${key.toUpperCase()} plan updated`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(null); }
    }

    async function reset(key) {
        if (!window.confirm(`Reset all overrides on the ${key} plan? This restores the built-in defaults.`)) return;
        setResetting(key);
        try {
            await api.delete(`/admin/subscription-plans/${key}/override`);
            toast.success(`${key.toUpperCase()} reset to defaults`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setResetting(null); }
    }

    async function retroApply(key) {
        if (!window.confirm(`Push this plan's default feature preset onto EVERY existing employer on the ${key} plan?\n\nCompanies already on the target preset are skipped. Reversible only by manually editing each employer in Feature Management.`)) return;
        try {
            const { data } = await api.post(`/admin/subscription-plans/${key}/apply-preset-to-existing`);
            toast.success(`Updated ${data.updated} employer${data.updated === 1 ? "" : "s"} (${data.skipped} already on preset).`);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    if (loading) {
        return (
            <div className="p-4 sm:p-6 max-w-6xl mx-auto">
                <div className="text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading plans…</div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-plans-page">
            <header className="mb-4 pb-4 border-b border-border">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Super admin</div>
                <h1 className="font-display font-black text-3xl text-ikb leading-tight">Subscription plans</h1>
                <p className="text-xs text-muted-foreground mt-1">
                    Edit prices, quotas, marketing bullets and the default feature preset that ships with each plan.
                    Changes take effect immediately for new signups and any employer whose plan is (re)assigned.
                </p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {plans.map((p) => (
                    <PlanCard
                        key={p.key}
                        plan={p}
                        draft={drafts[p.key] || {}}
                        presets={presets}
                        onField={(field, value) => setField(p.key, field, value)}
                        onSave={() => save(p.key)}
                        onReset={() => reset(p.key)}
                        onRetroApply={() => retroApply(p.key)}
                        saving={saving === p.key}
                        resetting={resetting === p.key}
                    />
                ))}
            </div>
        </div>
    );
}

function PlanCard({ plan, draft, presets, onField, onSave, onReset, onRetroApply, saving, resetting }) {
    const hasOverride = Object.keys(plan.override || {}).length > 0;
    const isPublic = draft.is_public !== false; // default True if unset
    const spotlight = draft.spotlight || plan.effective?.spotlight || {};
    function setSpotlight(k, v) {
        onField("spotlight", { ...spotlight, [k]: v });
    }
    return (
        <section className={`border bg-white ${draft.popular ? "border-ikb border-2" : "border-border"}`} data-testid={`plan-card-${plan.key}`}>
            <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-neutral-50">
                <div className="flex items-center gap-2 flex-wrap">
                    <Sparkles className="w-4 h-4 text-ikb" />
                    <span className="font-mono uppercase tracking-widest text-xs">{plan.key}</span>
                    {hasOverride && <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-amber-500 text-amber-700 bg-amber-50">override</span>}
                    {!isPublic && <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-rose-500 text-rose-700 bg-rose-50 inline-flex items-center gap-1"><EyeOff className="w-2.5 h-2.5" /> hidden</span>}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <label className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest cursor-pointer" title="Show on the public pricing page">
                        <input type="checkbox" checked={isPublic} onChange={(e) => onField("is_public", e.target.checked)} data-testid={`plan-public-${plan.key}`} />
                        {isPublic ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} Public
                    </label>
                    <label className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest cursor-pointer" title="Show 'Most popular' ribbon on this plan">
                        <input type="checkbox" checked={!!draft.popular} onChange={(e) => onField("popular", e.target.checked)} data-testid={`plan-popular-${plan.key}`} />
                        <Star className="w-3 h-3" /> Popular
                    </label>
                </div>
            </div>
            <div className="p-4 space-y-3">
                <Field label="Plan name">
                    <input type="text" value={draft.name || ""} onChange={(e) => onField("name", e.target.value)}
                        className="h-9 w-full border border-border bg-white text-sm px-2" data-testid={`plan-name-${plan.key}`} />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Price ₹ / month" icon={DollarSign}>
                        <input type="number" min="0" value={draft.price_inr ?? 0} onChange={(e) => onField("price_inr", e.target.value)}
                            className="h-9 w-full border border-border bg-white text-sm px-2 tabular-nums" data-testid={`plan-price-inr-${plan.key}`} />
                    </Field>
                    <Field label="Price $ / month" icon={DollarSign}>
                        <input type="number" min="0" value={draft.price_usd ?? 0} onChange={(e) => onField("price_usd", e.target.value)}
                            className="h-9 w-full border border-border bg-white text-sm px-2 tabular-nums" data-testid={`plan-price-usd-${plan.key}`} />
                    </Field>
                </div>

                <div className="grid grid-cols-3 gap-3">
                    <Field label="Seats" icon={Users}>
                        <input type="number" min="1" value={draft.seats ?? 1} onChange={(e) => onField("seats", e.target.value)}
                            className="h-9 w-full border border-border bg-white text-sm px-2 tabular-nums" data-testid={`plan-seats-${plan.key}`} />
                    </Field>
                    <Field label="Jobs" icon={Briefcase}>
                        <input type="number" min="0" value={draft.job_quota ?? 0} onChange={(e) => onField("job_quota", e.target.value)}
                            className="h-9 w-full border border-border bg-white text-sm px-2 tabular-nums" data-testid={`plan-jobs-${plan.key}`} />
                    </Field>
                    <Field label="Candidates" icon={Layers}>
                        <input type="number" min="0" value={draft.candidates_quota ?? 0} onChange={(e) => onField("candidates_quota", e.target.value)}
                            className="h-9 w-full border border-border bg-white text-sm px-2 tabular-nums" data-testid={`plan-cand-${plan.key}`} />
                    </Field>
                </div>

                <Field label="Marketing bullets (one per line)">
                    <textarea
                        rows={5}
                        value={(draft.features || []).join("\n")}
                        onChange={(e) => onField("features", e.target.value.split("\n").filter((l) => l.trim()))}
                        className="w-full border border-border bg-white text-xs px-2 py-1 font-mono"
                        data-testid={`plan-features-${plan.key}`}
                    />
                </Field>

                <Field label="Default feature preset (applied on plan assignment)">
                    <select
                        value={draft.default_preset_key || ""}
                        onChange={(e) => onField("default_preset_key", e.target.value)}
                        className="h-9 w-full border border-border bg-white text-sm px-2"
                        data-testid={`plan-preset-${plan.key}`}
                    >
                        <option value="">— No preset (uses global defaults) —</option>
                        {presets.map((pr) => (
                            <option key={pr.key} value={pr.key}>{pr.name}</option>
                        ))}
                    </select>
                    <div className="text-[10px] text-muted-foreground mt-1 flex items-center justify-between gap-2 flex-wrap">
                        <span>Companies signing up on this plan will automatically get this preset. Existing employers are unchanged.</span>
                        {draft.default_preset_key && (
                            <button
                                type="button"
                                onClick={onRetroApply}
                                className="inline-flex items-center gap-1 h-6 px-2 border border-ikb text-ikb bg-white text-[9px] font-mono uppercase tracking-widest hover:bg-ikb/5"
                                title="Push this preset onto every existing employer on this plan"
                                data-testid={`plan-retro-${plan.key}`}
                            >
                                <Users2 className="w-3 h-3" /> Apply to existing
                            </button>
                        )}
                    </div>
                </Field>

                {/* Spotlight feature-gate matrix — Priority Roles / campaigns entitlements */}
                <details className="border border-border" data-testid={`plan-spotlight-${plan.key}`}>
                    <summary className="cursor-pointer px-3 py-2 text-[10px] font-mono uppercase tracking-widest bg-neutral-50 hover:bg-neutral-100">
                        Spotlight (Priority Roles) entitlements
                    </summary>
                    <div className="p-3 space-y-2 text-xs">
                        <label className="flex items-center justify-between gap-3">
                            <span>Campaign limit</span>
                            <input
                                type="number"
                                min="0"
                                value={spotlight.campaigns_limit ?? ""}
                                onChange={(e) => setSpotlight("campaigns_limit", e.target.value === "" ? null : Number(e.target.value))}
                                placeholder="Unlimited"
                                className="h-8 w-24 border border-border bg-white text-xs px-2 tabular-nums text-right"
                                data-testid={`plan-spotlight-limit-${plan.key}`}
                            />
                        </label>
                        {[
                            ["custom_domain", "Custom domain"],
                            ["video_hero", "Video hero"],
                            ["custom_css", "Custom CSS"],
                            ["remove_footer", "Remove footer branding"],
                            ["sections_advanced", "Advanced sections (HTML/gallery/team)"],
                            ["featured_slot", "Featured hero-row slot"],
                        ].map(([k, label]) => (
                            <label key={k} className="flex items-center justify-between gap-3">
                                <span>{label}</span>
                                <input
                                    type="checkbox"
                                    checked={!!spotlight[k]}
                                    onChange={(e) => setSpotlight(k, e.target.checked)}
                                    data-testid={`plan-spotlight-${k}-${plan.key}`}
                                />
                            </label>
                        ))}
                    </div>
                </details>
            </div>

            <div className="px-4 py-3 border-t border-border bg-neutral-50 flex items-center gap-2 justify-end">
                {hasOverride && (
                    <button
                        type="button"
                        onClick={onReset}
                        disabled={resetting || saving}
                        className="h-8 px-3 border border-border bg-white text-[10px] font-mono uppercase tracking-widest hover:bg-neutral-100 inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid={`plan-reset-${plan.key}`}
                    >
                        {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                        Reset to defaults
                    </button>
                )}
                <button
                    type="button"
                    onClick={onSave}
                    disabled={saving || resetting}
                    className="h-8 px-3 border border-ikb bg-ikb text-white text-[10px] font-mono uppercase tracking-widest hover:bg-ikb/90 inline-flex items-center gap-1.5 disabled:opacity-60"
                    data-testid={`plan-save-${plan.key}`}
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Save
                </button>
            </div>
        </section>
    );
}

function Field({ label, icon: Icon, children }) {
    return (
        <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1 inline-flex items-center gap-1">
                {Icon && <Icon className="w-3 h-3" />}
                {label}
            </div>
            {children}
        </label>
    );
}
